import { almostZero } from "@xola/ui-kit";
import { SetFilterValuesFuncParams, ValueGetterParams } from "ag-grid-community";
import {
    getItemLineItemsByType,
    getItemLineItemsByTypes,
    getLineItemByCode,
    getPaymentLineItems,
    PurchaseItemStatus,
    PurchaseItemStatusCopy,
} from "common";
import { map, round, startCase, sumBy } from "lodash";
import React from "react";
import { ExperiencePurchaseItem, GuestStatus, LineItem, LineItemCode, LineItemType, Report } from "types";
import { Currency } from "../../../Common/Currency";
import { getGuestsByStatus } from "../../../PurchaseModification/CheckInPurchaseGuests/CheckInHelper";
import {
    CouponRenderer,
    PartnerRenderer,
    ProductRenderer,
    SourceCellRenderer,
    StatusCellRenderer,
} from "../ColumnTypes";
import { CustomFilter } from "../CustomFilters/CustomFilter";
import { CustomProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { TextFilterParams } from "../ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";

const getPurchaseItemStatuses = (): PurchaseItemStatus[] => {
    return [
        PurchaseItemStatus.HOLD,
        PurchaseItemStatus.PENDING_ACCEPT,
        PurchaseItemStatus.PENDING_MINIMUM,
        PurchaseItemStatus.CONFIRMED,
        PurchaseItemStatus.CANCELED,
        PurchaseItemStatus.DECLINED,
    ];
};

export const ReservationsReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(
        () =>
            getColumnDefs({
                report,
            }),
        [report],
    );

    return <CustomReport dateFields={["createdAt", "arrivalDate"]} report={report} columns={columns} />;
};

interface CustomTotalRendererProps {
    data: ExperiencePurchaseItem;
    lineItemType: LineItemType[];
    showZeroAsEmpty?: boolean;
}

const CustomTotalRenderer = ({ data, lineItemType, showZeroAsEmpty: showEmpty }: CustomTotalRendererProps) => {
    if (data) {
        const total = sumBy(data.lineItems, (lineItem) => (lineItemType.includes(lineItem.type) ? lineItem.amount : 0));
        const shouldShowValue = (showEmpty && total > 0) || !showEmpty;
        return shouldShowValue ? <Currency shouldRemoveTrailingZeroes={false}>{total}</Currency> : null;
    }

    return null;
};

interface LineItemTotalRendererProps {
    data: ExperiencePurchaseItem;
    lineItemType: LineItemType[];
    isCurrency?: boolean;
    colDef: ReportColumnDef;
}

interface sumByCallbackProps extends Omit<LineItemTotalRendererProps, "data" | "isCurrency"> {
    isTotal: boolean;
}

const sumByCallback =
    ({ colDef, lineItemType, isTotal }: sumByCallbackProps) =>
    (lineItem: LineItem) => {
        const isCorrectItemType = lineItemType.includes(lineItem.type);
        const isCorrectItemName = lineItem.name === colDef?.headerName;

        const totalValue = lineItem.amount;
        const quantityValue = lineItem.quantity ?? 0;

        if (isCorrectItemType && isCorrectItemName) {
            return isTotal ? totalValue : quantityValue;
        }

        return 0;
    };

const LineItemTotalRenderer = ({ data, lineItemType, colDef, isCurrency }: LineItemTotalRendererProps) => {
    if (data) {
        const { total, quantity } = {
            total: sumBy(data?.lineItems, sumByCallback({ colDef, lineItemType, isTotal: true })),
            quantity: sumBy(data?.lineItems, sumByCallback({ colDef, lineItemType, isTotal: false })),
        };

        const excludeLineItemTypes = new Set([LineItemType.TAX, LineItemType.FEE]);
        const hasExcludedType = lineItemType.some((type) => excludeLineItemTypes.has(type));
        // quantity will be null for these types, so not need to check quantity > 0 for these types
        const showCurrencyValue = hasExcludedType || quantity > 0;

        // If we displaying currency, and there is an actual line item with 0 amount, we will display 0.
        // If total amount is less than 0.01 we will display 0 as well.
        if (isCurrency && showCurrencyValue) {
            const value = almostZero(total) ? 0 : total;
            return <Currency shouldRemoveTrailingZeroes={false}>{value}</Currency>;
        }

        return quantity === 0 ? null : quantity;
    }

    return null;
};

const CheckInCountGetter = ({ data }: { data: ExperiencePurchaseItem }) => {
    let totalCount = 0;

    if (!data.guests) {
        return totalCount;
    }

    for (const guest of data.guests) {
        if (guest.checkInCount) {
            totalCount += guest.checkInCount;
            continue;
        }

        /**
         * IMPORTANT: For purchases before multi check-in functionality, `checkInCount` would not be available in a guest document.
         * Rather, if guest was checked-in, the `guestStatus` would be set to `arrived`.
         *
         * As, only single check-in functionality was there, we'll update the `totalCount` by 1
         **/
        if (guest.guestStatus === GuestStatus.ARRIVED) {
            totalCount += 1;
        }
    }

    return totalCount;
};

export enum PaymentStatus {
    PAID = "Paid",
    UNPAID = "Unpaid",
    PARTIALLY_PAID = "Partially Paid",
}
type GetColumnDefsParams = {
    report: Report;
};
const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "id",
        headerName: "Reservation ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "purchase.id",
        headerName: "Purchase ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "product",
        headerName: "Product",
        filter: CustomProductFilter,
        filterParams: {
            report,
        },
        minWidth: 150,
        cellRenderer: ProductRenderer,
    },

    {
        field: "createdAt",
        headerName: "Booking Date",
        type: "datetime",
        valueGetter: (params: ValueGetterParams) => params.data.purchase.createdAt,
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "arrivalDate",
        headerName: "Arrival Date",
        type: "date",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "arrivalTime",
        headerName: "Arrival Time",
        type: "arrivalTime",
        filter: "agTextColumnFilter",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "organizer.name",
        headerName: "Customer Name",
        minWidth: 200,
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "organizer.email",
        headerName: "Customer Email",
        minWidth: 200,
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "quantity",
        headerName: "Total Booked",
        type: "number",
        minWidth: 120,
        filterParams: {
            maxNumConditions: 1,
        },
        expand: {
            key: "lineItems.*",
            type: "number",
            minWidth: 130,
            headerGetter: (lineItems: LineItem[]) =>
                getMatchingHeaders(lineItems, [LineItemType.DEMOGRAPHIC, LineItemType.DEMOGRAPHIC_SECONDARY]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { lineItemType: [LineItemType.DEMOGRAPHIC, LineItemType.DEMOGRAPHIC_SECONDARY] },
        },
    },
    {
        field: "equipment",
        headerName: "Equipment Booked",
        type: "number",
        minWidth: 120,
        filter: false,
        // Equipment counts units, not people, so it stays out of the demographic breakdown above.
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) =>
            sumBy(getItemLineItemsByType(data, LineItemType.EQUIPMENT), "quantity"),
        expand: {
            key: "lineItems.*",
            type: "number",
            minWidth: 130,
            headerGetter: (lineItems: LineItem[]) => getMatchingHeaders(lineItems, [LineItemType.EQUIPMENT]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { lineItemType: [LineItemType.EQUIPMENT] },
        },
    },
    {
        field: "guests.checkInCount",
        headerName: "Total Check-Ins",
        minWidth: 120,
        type: "number",
        filter: false,
        valueGetter: CheckInCountGetter,
    },
    {
        field: "baseAmount",
        headerName: "Base Amount",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            const baseAmount = getLineItemByCode(data, LineItemCode.DEMOGRAPHIC)?.amount;
            if (!baseAmount) {
                return getItemLineItemsByTypes(data, [
                    LineItemType.DEMOGRAPHIC,
                    LineItemType.DEMOGRAPHIC_SECONDARY,
                    LineItemType.EQUIPMENT,
                ]).reduce((sum, lineItem) => sum + (lineItem.includeInAmount ? lineItem?.amount ?? 0 : 0), 0);
            }

            return baseAmount ?? 0;
        },
    },
    {
        field: "revenue",
        headerName: "Revenue",
        type: "currency",
        filter: false,
    },
    {
        field: "paymentStatus",
        headerName: "Payment Status",
        filter: false,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            const paymentLineItems = getPaymentLineItems(data.purchase);
            const amountPaid = round(
                sumBy(paymentLineItems, (lineItem) => -1 * lineItem.amount),
                2,
            );

            if (amountPaid >= data.purchase.amount) {
                return PaymentStatus.PAID;
            }

            if (amountPaid > 0) {
                return PaymentStatus.PARTIALLY_PAID;
            }

            return PaymentStatus.UNPAID;
        },
    },
    {
        field: "amount",
        headerName: "Total Value",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "fees",
        headerName: "Taxes & Fees Total",
        type: "currency",
        minWidth: 160,
        cellRendererParams: { lineItemType: [LineItemType.TAX, LineItemType.FEE] },
        cellRenderer: CustomTotalRenderer,
        filter: false,
        expand: {
            key: "lineItems.*",
            type: ["currency"],
            minWidth: 170,
            maxWidth: 200,
            headerGetter: (lineItems: LineItem[]) =>
                getMatchingHeaders(lineItems, [LineItemType.TAX, LineItemType.FEE]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { lineItemType: [LineItemType.TAX, LineItemType.FEE], isCurrency: true },
        },
    },
    {
        field: "addon",
        headerName: "Add-ons Total",
        minWidth: 200,
        cellRendererParams: { lineItemType: [LineItemType.ADDON, LineItemType.PICKUP], showZeroAsEmpty: true },
        cellRenderer: CustomTotalRenderer,
        expand: {
            key: "lineItems.*",
            type: ["currency"],
            minWidth: 170,
            maxWidth: 200,
            headerGetter: (lineItems: LineItem[]) =>
                getMatchingHeaders(lineItems, [LineItemType.ADDON, LineItemType.PICKUP]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { lineItemType: [LineItemType.ADDON, LineItemType.PICKUP], isCurrency: true },
        },
    },
    {
        field: "lineItems.coupon",
        headerName: "Coupon Campaign",
        filter: CustomFilter,
        filterParams: {
            report,
            collection: "coupons",
        },
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            const { couponId } = getCouponInfo(data);
            return couponId ?? "";
        },
        minWidth: 200,
        cellRenderer: CouponRenderer,
    },
    {
        field: "lineItems.code",
        headerName: "Coupon Code",
        minWidth: 150,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        filterParams: {
            maxNumConditions: 1,
        },
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            const { code } = getCouponInfo(data);
            return code ?? "";
        },
    },
    {
        field: "couponAmount",
        headerName: "Coupon Amount",
        type: "currency",
        filter: false,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            if (data) {
                const couponLineItem = data?.lineItems.find((lineItem) => !!lineItem.couponCode);
                return couponLineItem ? -1 * couponLineItem.amount : "";
            }

            return "";
        },
    },
    // TODO: If affiliates is enabled
    {
        field: "partner",
        headerName: "Partner Name",
        minWidth: 150,
        maxWidth: 250,
        filter: CustomFilter,
        cellRenderer: PartnerRenderer,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            return data?.partner?.id ?? "";
        },
        filterParams: {
            report,
            collection: "partners",
            enableEmptySelection: true,
        },
    },
    {
        field: "affiliateCommission",
        headerName: "Partner Commission",
        type: "currency",
        minWidth: 180,
        maxWidth: 200,
        filter: false,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            if (data) {
                const { commissionLineItem } = getAffiliateInfo(data);
                return commissionLineItem ? -1 * commissionLineItem.amount : "";
            }

            return "";
        },
    },
    {
        field: "affiliateDiscount",
        headerName: "Partner Discount",
        type: "currency",
        maxWidth: 200,
        filter: false,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            if (data) {
                const { discountLineItem } = getAffiliateInfo(data);
                return discountLineItem ? -1 * discountLineItem.amount : "";
            }

            return "";
        },
    },
    {
        field: "adjustmentAmount",
        headerName: "Adjustments",
        type: "currency",
        maxWidth: 200,
        filter: false,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            if (data) {
                const adjustmentLineItemTypes = [LineItemType.ADJUSTMENT, LineItemType.TAXED_ADJUSTMENT];
                const adjustmentLineItems = getItemLineItemsByTypes(data, adjustmentLineItemTypes);
                const amount = sumBy(adjustmentLineItems, (lineItem) => {
                    return lineItem.includeInAmount ? lineItem.amount : 0;
                });

                return amount ?? "";
            }

            return "";
        },
    },
    {
        field: "source",
        headerName: "Source",
        type: "source",
        cellRenderer: SourceCellRenderer,
    },
    {
        field: "status",
        headerName: "Status",
        minWidth: 150,
        filter: "agSetColumnFilter",
        filterParams: {
            values: (params: SetFilterValuesFuncParams) => {
                params.success(getPurchaseItemStatuses());
            },
            cellRenderer: StatusCellRenderer,
        },
        cellRenderer: StatusCellRenderer,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            return PurchaseItemStatusCopy[data.status];
        },
    },
    {
        field: "guestStatus",
        headerName: "Guest Status",
        minWidth: 200,
        filter: "agSetColumnFilter",
        filterParams: {
            values: (params: SetFilterValuesFuncParams) => {
                params.success(Object.values(GuestStatus));
            },
            cellRenderer: StatusCellRenderer,
        },
        cellRenderer: StatusCellRenderer,
        valueGetter: ({ data }: { data: ExperiencePurchaseItem }) => {
            const statuses = getGuestsByStatus(data);
            const values = map(statuses, (count, status) => (count > 0 ? startCase(status) : null)).filter((s) => !!s);
            return values.join(", ");
        },
    },
    {
        field: "purchase.createdBy",
        headerName: "Created By",
        type: "user",
        minWidth: 200,
        filter: false,
    },
    {
        field: "shortCode",
        headerName: "Confirmation ID",
        maxWidth: 150,
    },
    {
        field: "purchase.tags",
        headerName: "Tags",
        type: "tags",
    },
];

const getCouponInfo = (data: ExperiencePurchaseItem) => {
    const couponLineItem = data?.lineItems.find((lineItem) => !!lineItem.couponCode);
    const couponId = couponLineItem?.coupon?.id;
    const code = couponLineItem?.couponCode?.code;

    return { code, couponId };
};

const getAffiliateInfo = (data: ExperiencePurchaseItem) => {
    const discountLineItem = data?.lineItems.find((lineItem) => lineItem.type === LineItemType.AFFILIATE_DISCOUNT);
    const commissionLineItem = data?.lineItems.find((lineItem) => lineItem.type === LineItemType.AFFILIATE_COMMISSION);
    return { discountLineItem, commissionLineItem };
};

const getMatchingHeaders = (lineItems: LineItem[], type: LineItemType[]): string[] => {
    return lineItems.filter((lineItem) => type.includes(lineItem.type)).map((lineItem) => lineItem.name);
};
